import { ButtonBase, Typography } from "@mui/material";
import { CUSTOM_COLORS, ColorToken } from "@rmh/ui-config";
import type { ReactNode } from "react";
import { useState } from "react";
import { reverse } from "lodash";
import { useTranslations } from "next-intl";

type Rating = "0" | "1" | "2" | "3" | "4" | "5" | "";

const ratingColors = [
  {
    backgroundColor: CUSTOM_COLORS[ColorToken.GREEN][700],
    borderColor: CUSTOM_COLORS[ColorToken.GREEN][50],
  },
  {
    backgroundColor: CUSTOM_COLORS[ColorToken.GREEN][600],
    borderColor: CUSTOM_COLORS[ColorToken.GREEN][100],
  },
  {
    backgroundColor: CUSTOM_COLORS[ColorToken.YELLOW][500],
    borderColor: CUSTOM_COLORS[ColorToken.YELLOW][200],
  },
  {
    backgroundColor: CUSTOM_COLORS[ColorToken.ORANGE][400],
    borderColor: CUSTOM_COLORS[ColorToken.ORANGE][100],
  },
  {
    backgroundColor: CUSTOM_COLORS[ColorToken.RED][500],
    borderColor: CUSTOM_COLORS[ColorToken.RED][100],
  },
  {
    backgroundColor: CUSTOM_COLORS[ColorToken.RED][700],
    borderColor: CUSTOM_COLORS[ColorToken.RED][200],
  },
];

interface RatingConfig {
  index: number;
  label: Rating;
  value: number;
  scaleLabel: string;
  backgroundColor: string;
  borderColor: string;
}

interface GetRatingConfigParams {
  start: number;
  isReversed: boolean;
  labels: string[];
}

const getRatingConfigs = ({
  start,
  isReversed,
  labels,
}: GetRatingConfigParams): RatingConfig[] => {
  const count = labels.length;
  return Array.from({ length: count }, (_, i) => {
    const colors = isReversed
      ? reverse(ratingColors.slice(start, start + count))
      : ratingColors.slice(start, start + count);
    return {
      index: i,
      label: (start + i).toString() as Rating,
      value: start + i,
      backgroundColor: colors[i].backgroundColor,
      borderColor: colors[i].borderColor,
      scaleLabel: labels[i],
    };
  });
};

interface RatingLabelProps {
  isCurrentQuestion: boolean;
  config?: RatingConfig;
}

const RatingLabel = ({ isCurrentQuestion, config }: RatingLabelProps) => {
  const t = useTranslations();
  const transition = "transition-all duration-[500ms]";
  const questionTransitionStart = !isCurrentQuestion ? "h-0 opacity-0" : "";

  let content: ReactNode = (
    <Typography className="text-center" variant="text_md_m">
      {t("Prom.selectOptionBelow")}
    </Typography>
  );

  if (config) {
    content = (
      <div className="flex flex-col justify-center gap-6 align-middle">
        <Typography className="text-center" variant="bodyS">
          {config.scaleLabel}
        </Typography>
      </div>
    );
  }

  return (
    <div
      className={`flex w-full flex-col justify-center ${transition} ${questionTransitionStart}`}
    >
      {content}
    </div>
  );
};

interface RatingSelectorProps {
  scaleStartLabel: string;
  scaleEndLabel: string;
  transitionComplete: boolean;
  instructions: string;
  start: number;
  questionText: string;
  questionNumber: number;
  ratingLabels?: string[];
  value?: number;
  isCurrentQuestion: boolean;
  isRatingReversed: boolean;
  isNavigable: boolean;
  onSelect: (value: number) => void;
  onOtherQuestionClick: (value: number) => void;
}

interface RatingButtonProps {
  config: RatingConfig;
  value?: number;
  questionNumber: number;
  isCurrentQuestion: boolean;
  isReady: boolean;
  onClick: (index: number) => void;
  onMouseEnter: (index: number) => void;
  onMouseLeave: () => void;
}

const RatingButton = ({
  config,
  questionNumber,
  isCurrentQuestion,
  onClick,
  onMouseEnter,
  onMouseLeave,
  value,
}: RatingButtonProps) => {
  const isSelected = value === config.value;

  return (
    <ButtonBase
      aria-label={`${questionNumber}-${config.label}`}
      className="flex h-12 w-12 flex-col justify-center md:h-14 md:w-14 lg:h-16 lg:w-16"
      data-cy={config.label}
      disabled={!isCurrentQuestion}
      key={config.label}
      onClick={() => {
        onClick(config.index);
      }}
      onMouseEnter={() => {
        onMouseEnter(config.index);
      }}
      onMouseLeave={onMouseLeave}
      sx={{
        ...(isSelected
          ? {
              backgroundColor: config.backgroundColor,
            }
          : {
              bgcolor: "rgba(0, 0, 0, 0.1)",
              "&:hover": {
                backgroundColor: CUSTOM_COLORS[ColorToken.DEEP_BLUE][50],
                opacity: 0.5,
              },
            }),
        borderRadius: "0.5rem",
        "&:focus-visible": {
          outline: `2px solid ${CUSTOM_COLORS["rmh-blue"][500]}`,
          outlineOffset: 2,
        },
      }}
      tabIndex={isCurrentQuestion ? 0 : -1}
    >
      <Typography className="text-center" variant="display_xs_sb">
        {config.label}
      </Typography>
    </ButtonBase>
  );
};

export const RatingSelector = ({
  scaleStartLabel,
  scaleEndLabel,
  transitionComplete,
  instructions,
  start = 0,
  questionText,
  questionNumber,
  ratingLabels = [],
  value,
  isCurrentQuestion,
  isRatingReversed,
  isNavigable,
  onSelect,
  onOtherQuestionClick,
}: RatingSelectorProps) => {
  const ratingConfigs = getRatingConfigs({
    start,
    labels: ratingLabels,
    isReversed: isRatingReversed,
  });

  const selectedIndex = ratingConfigs.findIndex(
    (config) => config.value === value
  );
  // const selectedIndex = value !== undefined ? options.findIndex({}) : undefined;
  const [hoveringIndex, setHoveringIndex] = useState<number | undefined>();

  const handleOnRatingMouseEnter = (index: number) => {
    if (transitionComplete) {
      setHoveringIndex(index);
    }
  };

  const handleOnRatingMouseLeave = () => {
    setHoveringIndex(undefined);
  };

  const handleOnRatingClick = (index: number) => {
    const config = ratingConfigs[index];

    onSelect(config.value);
  };

  const handleQuestionClick = () => {
    if (!isCurrentQuestion) {
      onOtherQuestionClick(questionNumber);
    }
  };

  return (
    <div
      className="m-auto flex h-full w-full flex-col items-center gap-8 overflow-auto"
      data-cy="rating_selector"
    >
      <div
        className={`text-rmh-white flex h-full w-full grow flex-col justify-between rounded-2xl bg-gradient-to-b p-8 transition-all duration-[500ms] ease-in-out ${
          isCurrentQuestion
            ? "from-[#0088CE] to-[#15388C]"
            : `min-h-fit scale-y-[0.85] justify-end from-[#2B478B] to-[#0B1325] opacity-50 ${
                isNavigable
                  ? "cursor-pointer hover:from-[#0088CE] hover:to-[#15388C] hover:opacity-100"
                  : ""
              }`
        }`}
        {...(!isCurrentQuestion &&
          isNavigable && {
            onClick: handleQuestionClick,
            onKeyDown: handleQuestionClick,
            tabIndex: -1,
            role: "button",
          })}
      >
        <div className="flex flex-col gap-2">
          <Typography variant="bodyL">{instructions}</Typography>
          <Typography variant="headingH6">{questionText}</Typography>
        </div>
        <RatingLabel
          config={ratingConfigs[hoveringIndex ?? selectedIndex]}
          isCurrentQuestion={isCurrentQuestion}
        />
        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full justify-between">
            <Typography variant="bodyS">{scaleStartLabel}</Typography>
            <Typography variant="bodyS">{scaleEndLabel}</Typography>
          </div>
          <div className="flex w-full justify-between">
            {ratingConfigs.map((config) => (
              <RatingButton
                config={config}
                isCurrentQuestion={isCurrentQuestion}
                isReady={transitionComplete}
                key={config.label}
                onClick={handleOnRatingClick}
                onMouseEnter={handleOnRatingMouseEnter}
                onMouseLeave={handleOnRatingMouseLeave}
                questionNumber={questionNumber}
                value={value}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
